import { DOCK_POSITION_COPY } from '@/lib/copy';
import type { StatusTone } from '@/lib/dock-status';
import type { DockPositionCheck } from '@/types/dock';

/**
 * What a surface shows for a dock's pin check (`GET /api/docks` `position_check`).
 *
 * One mapping for every surface - the fleet table and the COP dock panel - so an operator never
 * reads two answers for one dock (D14):
 * - a drifted pin is AMBER: returns and landings aim a few metres off;
 * - a dock with NO pin is RED: landing checks have nothing to measure against, which is a broken
 *   registry, not a degraded one;
 * - a pin nobody has been able to check yet is NEUTRAL, never green.
 */
export interface DockPositionView {
  tone: StatusTone;
  /** Badge text on the COP dock panel. */
  label: string;
  /** Badge text in the fleet table, whose column is already headed Position. Null means show a dash. */
  compactLabel: string | null;
  /** The short line a table row shows under its badge when there is something to act on. */
  hint: string | null;
  /** The full sentence: shown on the COP dock panel when actionable, and read to screen readers everywhere. */
  detail: string;
  /** Whether the operator has something to act on. */
  actionable: boolean;
}

const COMPASS = ['N', 'NE', 'E', 'SE', 'S', 'SW', 'W', 'NW'] as const;

/** The eight-point compass direction for a bearing in degrees - the same words the server's alert uses. */
export function compassPoint(bearingDeg: number): string {
  const normalised = ((bearingDeg % 360) + 360) % 360;
  return COMPASS[Math.round(normalised / 45) % 8];
}

export function dockPositionView(check: DockPositionCheck | undefined): DockPositionView {
  const copy = DOCK_POSITION_COPY;
  switch (check?.status) {
    case 'ok':
      return {
        tone: 'green',
        label: copy.ok,
        compactLabel: copy.compactOk,
        hint: null,
        detail: copy.okDetail({ distanceM: check.distanceM, windows: check.suggestion.windows, warnM: check.warnM }),
        actionable: false,
      };
    case 'drift': {
      const direction = compassPoint(check.bearingDeg);
      return {
        tone: 'amber',
        label: copy.drift({ distanceM: check.distanceM }),
        compactLabel: copy.compactDrift({ distanceM: check.distanceM }),
        hint: copy.driftHint({ distanceM: check.distanceM, direction }),
        detail: copy.driftDetail({
          distanceM: check.distanceM,
          direction,
          windows: check.suggestion.windows,
          clearM: check.clearM,
        }),
        actionable: true,
      };
    }
    case 'no_pin':
      return {
        tone: 'red',
        label: copy.noPin,
        compactLabel: copy.compactNoPin,
        hint: copy.noPinHint,
        detail: check.suggestion ? copy.noPinWithSuggestion : copy.noPinDetail,
        actionable: true,
      };
    case 'insufficient_evidence':
      return {
        tone: 'neutral',
        label: copy.notChecked,
        compactLabel: null,
        hint: null,
        detail: copy.notCheckedDetail({ windows: check.windows }),
        actionable: false,
      };
    default:
      return {
        tone: 'neutral',
        label: copy.notChecked,
        compactLabel: null,
        hint: null,
        detail: copy.unknownDetail,
        actionable: false,
      };
  }
}
